import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  BarChart3, DollarSign, CreditCard, Receipt, UserPlus, UserX, TrendingUp,
  Download, Loader2, FileSpreadsheet, FileText, FileDown,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell, ComposedChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const COLORS = ["hsl(var(--primary))", "hsl(var(--destructive))", "hsl(var(--warning))"];
const CURRENCY_SYMBOL = { USD: "$", EUR: "€", GBP: "£", MKD: "ден", RSD: "дин", CHF: "CHF" };

const tooltipStyle = {
  backgroundColor: "hsl(var(--popover))",
  border: "1px solid hsl(var(--border))",
  borderRadius: "8px",
  fontSize: "12px",
};

const RANGES = [
  { key: "today", label: "Today" },
  { key: "week", label: "7 Days" },
  { key: "month", label: "Month" },
  { key: "year", label: "Year" },
  { key: "custom", label: "Custom" },
];

function StatCard({ icon: Icon, label, value, sub, tone = "primary", testid }) {
  const tones = {
    primary: "text-primary bg-primary/10",
    success: "text-success bg-success/10",
    destructive: "text-destructive bg-destructive/10",
    warning: "text-warning bg-warning/10",
    accent: "text-accent bg-accent/10",
  };
  return (
    <Card className="p-5 border-border bg-card transition-transform duration-200 hover:-translate-y-0.5" data-testid={testid}>
      <div className={`grid place-items-center h-10 w-10 rounded-md ${tones[tone]}`}>
        <Icon className="h-5 w-5" aria-hidden="true" />
      </div>
      <p className="mt-4 text-3xl font-display font-bold tracking-tight tabular-nums">{value}</p>
      <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground mt-1">{label}</p>
      {sub && <p className="text-xs text-muted-foreground mt-0.5">{sub}</p>}
    </Card>
  );
}

function ChartCard({ title, children, action }) {
  return (
    <Card className="p-5 border-border bg-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-semibold text-lg">{title}</h3>
        {action}
      </div>
      <div className="h-72">{children}</div>
    </Card>
  );
}

export default function Reports() {
  const { user } = useAuth();
  const isAdmin = user.role === "super_admin";
  const [range, setRange] = useState("month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ range });
      if (range === "custom") {
        if (start) params.set("start", start);
        if (end) params.set("end", end);
      }
      const { data } = await api.get(`/reports?${params.toString()}`);
      setReport(data);
    } catch {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (range === "custom" && (!start || !end)) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, start, end]);

  const cur = report?.currency || "USD";
  const sym = CURRENCY_SYMBOL[cur] || "";
  const money = (n) => {
    const v = Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return sym === "$" || sym === "€" || sym === "£" ? `${sym}${v}` : `${v} ${sym || cur}`;
  };
  const num = (n) => Number(n || 0).toLocaleString();

  const doExport = async (fmt) => {
    setExporting(fmt);
    try {
      const params = new URLSearchParams({ format: fmt, range });
      if (range === "custom") { if (start) params.set("start", start); if (end) params.set("end", end); }
      const { data } = await api.get(`/export/reports?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url; a.download = `report_${range}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Report exported (${fmt.toUpperCase()})`);
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting("");
    }
  };

  const s = report?.summary;

  return (
    <div data-testid="reports-page">
      <PageHeader overline="Analytics" title="Earnings & Reports" description="Track revenue, credits and customer growth across your operation.">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" disabled={!report || !!exporting} data-testid="reports-export-trigger">
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />} Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-popover">
            <DropdownMenuItem onClick={() => doExport("xlsx")} data-testid="reports-export-xlsx"><FileSpreadsheet className="h-4 w-4 mr-2" /> Excel</DropdownMenuItem>
            <DropdownMenuItem onClick={() => doExport("pdf")} data-testid="reports-export-pdf"><FileText className="h-4 w-4 mr-2" /> PDF</DropdownMenuItem>
            <DropdownMenuItem onClick={() => doExport("csv")} data-testid="reports-export-csv"><FileDown className="h-4 w-4 mr-2" /> CSV</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </PageHeader>

      {/* Range selector */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <Tabs value={range} onValueChange={setRange}>
          <TabsList data-testid="reports-range-tabs">
            {RANGES.map((r) => (
              <TabsTrigger key={r.key} value={r.key} data-testid={`range-${r.key}`}>{r.label}</TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
        {range === "custom" && (
          <div className="flex items-center gap-2">
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="w-[150px]" data-testid="range-start" />
            <span className="text-muted-foreground text-sm">→</span>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="w-[150px]" data-testid="range-end" />
          </div>
        )}
      </div>

      {loading || !report ? (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-32 rounded-lg" />)}
        </div>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard icon={DollarSign} label="Total Earnings" value={money(s.total_money)} tone="success" testid="report-money" />
            <StatCard icon={CreditCard} label="Credits Sold" value={num(s.total_credits)} tone="primary" testid="report-credits" />
            <StatCard icon={Receipt} label="Sales" value={num(s.total_sales)} sub={`avg ${money(s.avg_sale_money)}`} tone="accent" testid="report-sales" />
            <StatCard icon={UserPlus} label="New Customers" value={num(s.new_customers)} tone="primary" testid="report-new" />
            <StatCard icon={UserX} label="Expired" value={num(s.expired_customers)} tone="destructive" testid="report-expired" />
            <StatCard icon={TrendingUp} label="Active Now" value={num(s.active_customers)} tone="success" testid="report-active" />
          </div>

          {/* Revenue over time */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
            <div className="lg:col-span-2">
              <ChartCard title="Earnings & Credits Over Time">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={report.revenue_series}>
                    <defs>
                      <linearGradient id="moneyGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="hsl(var(--success))" stopOpacity={0.35} />
                        <stop offset="100%" stopColor="hsl(var(--success))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="label" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                    <YAxis yAxisId="l" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis yAxisId="r" orientation="right" stroke="hsl(var(--muted-foreground))" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip contentStyle={tooltipStyle} formatter={(v, n) => (n === "money" ? money(v) : `${num(v)} cr`)} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Area yAxisId="l" type="monotone" dataKey="money" name="money" stroke="hsl(var(--success))" strokeWidth={2} fill="url(#moneyGrad)" />
                    <Line yAxisId="r" type="monotone" dataKey="credits" name="credits" stroke="hsl(var(--primary))" strokeWidth={2} dot={false} />
                  </ComposedChart>
                </ResponsiveContainer>
              </ChartCard>
            </div>
            <ChartCard title="Subscription Status">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={report.status_breakdown} dataKey="value" nameKey="name" innerRadius={55} outerRadius={85} paddingAngle={3}>
                    {report.status_breakdown.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={tooltipStyle} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex justify-center gap-4 -mt-8">
                {report.status_breakdown.map((st, i) => (
                  <div key={st.name} className="flex items-center gap-1.5 text-xs">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i] }} /> {st.name}
                  </div>
                ))}
              </div>
            </ChartCard>
          </div>

          {/* Customers trend + By plan */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
            <ChartCard title="New vs Expired Customers (6 mo)">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={report.customers_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "hsl(var(--secondary))" }} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="new" name="New" fill="hsl(var(--success))" radius={[4, 4, 0, 0]} maxBarSize={36} />
                  <Bar dataKey="expired" name="Expired" fill="hsl(var(--destructive))" radius={[4, 4, 0, 0]} maxBarSize={36} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>

            <Card className="p-5 border-border bg-card">
              <h3 className="font-display font-semibold text-lg mb-4">Earnings by Plan</h3>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>Plan</TableHead>
                      <TableHead className="text-right">Sales</TableHead>
                      <TableHead className="text-right">Credits</TableHead>
                      <TableHead className="text-right">Earnings</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.by_plan.length === 0 ? (
                      <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No sales in this period.</TableCell></TableRow>
                    ) : report.by_plan.map((p) => (
                      <TableRow key={p.plan_name} data-testid={`plan-row-${p.plan_name}`}>
                        <TableCell><Badge variant="secondary">{p.plan_name}</Badge></TableCell>
                        <TableCell className="text-right tabular-nums">{num(p.count)}</TableCell>
                        <TableCell className="text-right tabular-nums text-primary">{num(p.credits)}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium text-success">{money(p.money)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </div>

          {/* By reseller (admin only) */}
          {isAdmin && report.by_reseller && (
            <Card className="p-5 border-border bg-card mt-4" data-testid="by-reseller-card">
              <h3 className="font-display font-semibold text-lg mb-4">Earnings by Reseller</h3>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead>Reseller</TableHead>
                      <TableHead className="text-right">Sales</TableHead>
                      <TableHead className="text-right">Credits</TableHead>
                      <TableHead className="text-right">Earnings</TableHead>
                      <TableHead className="text-right hidden sm:table-cell">Balance</TableHead>
                      <TableHead className="text-right hidden sm:table-cell">Customers</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {report.by_reseller.length === 0 ? (
                      <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No reseller sales in this period.</TableCell></TableRow>
                    ) : report.by_reseller.slice(0, 50).map((r) => (
                      <TableRow key={r.seller_id} data-testid={`reseller-row-${r.seller_id}`}>
                        <TableCell className="font-medium">{r.name}{r.role === "super_admin" && <Badge variant="outline" className="ml-2 border-accent/40 text-accent">Admin</Badge>}</TableCell>
                        <TableCell className="text-right tabular-nums">{num(r.count)}</TableCell>
                        <TableCell className="text-right tabular-nums text-primary">{num(r.credits)}</TableCell>
                        <TableCell className="text-right tabular-nums font-medium text-success">{money(r.money)}</TableCell>
                        <TableCell className="text-right tabular-nums hidden sm:table-cell">{num(r.credits_balance)} cr</TableCell>
                        <TableCell className="text-right tabular-nums hidden sm:table-cell">{num(r.customer_count)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
